import { Check, MapPin, User } from "lucide-react";
import { cn } from "~/widgets/lib/utils";
import { groupByMonth, type SessionRow } from "./sessions";

// A list of sessions to choose from, grouped under month headings (v1's
// SessionDateList + SessionDateRow), for the reschedule page (one choice)
// and a bundle's dates (several). Rows are buttons, so the list works from
// the keyboard; it holds no state of its own.

export function SessionPicker({
  rows,
  timezone,
  selected,
  isDisabled,
  onToggle,
  multiple = false,
}: {
  rows: SessionRow[];
  /** The zone months are read in. */
  timezone: string;
  selected: string[];
  isDisabled: (row: SessionRow) => boolean;
  onToggle: (id: string) => void;
  multiple?: boolean;
}) {
  const groups = groupByMonth(rows, timezone);
  return (
    <div className="space-y-6" role="group" aria-label={multiple ? "Choose dates" : "Choose a date"}>
      {groups.map((group) => (
        <section key={group.key} className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</h3>
          <ul className="space-y-2">
            {group.items.map((row) => {
              const isSelected = selected.includes(row.id);
              const disabled = isDisabled(row);
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    disabled={disabled}
                    onClick={() => onToggle(row.id)}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-lg border bg-background p-4 text-left transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isSelected ? "border-primary ring-1 ring-primary bg-primary/5" : "hover:bg-muted/40",
                      disabled && "cursor-not-allowed opacity-50 hover:bg-background",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mt-0.5 flex size-5 shrink-0 items-center justify-center border",
                        multiple ? "rounded" : "rounded-full",
                        isSelected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
                      )}
                    >
                      {isSelected && <Check className="size-3.5" />}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                      <span className="min-w-0">
                        <span className="block font-medium">{row.date}</span>
                        <span className="block text-sm text-muted-foreground">
                          {row.time}
                          {row.offset && <small className="ml-1">({row.offset})</small>}
                        </span>
                      </span>
                      <span className="flex flex-col gap-1 text-sm text-muted-foreground sm:items-end sm:text-right">
                        <span className={cn(row.soldOut && "font-medium text-destructive")}>{row.availability}</span>
                        {row.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="size-3.5" aria-hidden="true" />
                            {row.location}
                          </span>
                        )}
                        {row.tutorName && (
                          <span className="inline-flex items-center gap-1.5">
                            {row.tutorImageUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element -- a web component, not a Next page
                              <img src={row.tutorImageUrl} alt="" className="size-5 rounded-full object-cover" />
                            ) : (
                              <User className="size-3.5" aria-hidden="true" />
                            )}
                            {row.tutorName}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
